import { Component, OnInit } from '@angular/core';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { dateStringFromISO8601, timeStringFromISO8601 } from '../../../../../../utils';

import { AccountService } from '../../../../../../services/account/account.service';
import { DocumentPendingStaffApproval } from '../../../../../../models/inbox';
import { InboxService } from '../../../../../../services/inbox/inbox.service';
import {
  ReviewDocumentPendingStaffApprovalModalComponent
} from './modals/review-document-pending-staff-approval-modal/review-document-pending-staff-approval-modal.component';
import {
  ViewDismissedDocumentPendingStaffApprovalModalComponent
} from './modals/view-dismissed-document-pending-staff-approval-modal/view-dismissed-document-pending-staff-approval-modal.component';
import { Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';


const ACTIVE_DOCUMENTS_PAGE_SIZE = 20;


@Component({
  selector: 'app-inbox-documents-pending-staff-approval',
  templateUrl: './inbox-documents-pending-staff-approval.component.html',
  styleUrls: ['./inbox-documents-pending-staff-approval.component.css']
})
export class InboxDocumentsPendingStaffApprovalComponent implements OnInit {

  public activeDocuments: DocumentPendingStaffApproval[];
  public dismissedDocuments: DocumentPendingStaffApproval[];

  public activeDocsPageNum = 1;

  public loadingActiveDocuments = true;
  public loadingDismissedDocuments = true;

  public serverError: number = null;

  public activeDocsReloadSubject = new Subject<void>();

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public dateStringFromISO8601 = dateStringFromISO8601;
  public timeStringFromISO8601 = timeStringFromISO8601;

  constructor(
    public accountService: AccountService,
    public inboxService: InboxService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    this.activeDocsReloadSubject.pipe(
      map(() => { this.loadingActiveDocuments = true; }),
      debounceTime(300)
    ).subscribe(() => {
      this.loadActiveDocuments();
    });

    this.loadActiveDocuments();
    this.loadDismissedDocuments();
    this.inboxService.loadInboxCounts(true).then();
  }

  public activeDocumentsPresent() {
    return this.activeDocuments && (this.activeDocuments.length > 0);
  }

  public dismissedDocumentsPresent() {
    return this.dismissedDocuments && (this.dismissedDocuments.length > 0);
  }

  public openReviewActiveDocumentModal(document: DocumentPendingStaffApproval) {
    const modalRef = this.modalService.open(ReviewDocumentPendingStaffApprovalModalComponent);
    modalRef.componentInstance.documentId = document.document_id;
    modalRef.componentInstance.showHideOption = true;
    modalRef.componentInstance.savedCallback = () => {
      this.loadActiveDocuments();
      this.loadDismissedDocuments();
    };
  }

  public openViewDismissedDocumentModal(document: DocumentPendingStaffApproval) {
    const modalRef = this.modalService.open(ViewDismissedDocumentPendingStaffApprovalModalComponent);
    modalRef.componentInstance.documentId = document.document_id;
  }

  /**
   * Update the current page number to the given value and load the page of
   * results.
   *
   * @param newPageNumber - The new page number.
   */
  public setActiveDocsPage(newPageNumber: number) {
    this.activeDocsPageNum = newPageNumber;
    this.activeDocsReloadSubject.next();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (this.inboxService.getInboxCounts() === null) { return 1; }
    return Math.ceil(
      this.inboxService.getInboxCounts().documents_pending_staff_approval /
      ACTIVE_DOCUMENTS_PAGE_SIZE
    );
  }

  private loadActiveDocuments() {
    this.loadingActiveDocuments = true;

    this.inboxService
      .getActiveDocumentsPendingStaffApproval(this.activeDocsPageNum)
      .then(documents => {
        this.activeDocuments = documents;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingActiveDocuments = false; });
  }

  private loadDismissedDocuments() {
    this.loadingDismissedDocuments = true;

    this.inboxService
      .getRecentlyDismissedDocumentsPendingStaffApproval()
      .then(documents => {
        this.dismissedDocuments = documents;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingDismissedDocuments = false; });
  }

}
